/**
 * Integrations.tsx — CRM connection surfaces.
 *
 * Route: /#/settings/integrations
 *
 * No real OAuth. The cards render Disconnected and a click opens a modal
 * that points the user at sales@mi6group.com. The point of this page is
 * to PROVE that AIP coexists with Salesforce / HubSpot / MS Dynamics —
 * the real read/write sync gets wired in pilot.
 *
 * When integration is wired:
 *   - Replace `cardState[provider].status === "disconnected"` with live state
 *     pulled from a server endpoint or `integrations` table.
 *   - Replace handleConnect to redirect to the CRM OAuth start URL.
 */

import { useState, useCallback, useEffect } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { X } from "lucide-react";
import IntegrationsPanel from "@/components/settings/IntegrationsPanel";

type Provider = "salesforce" | "hubspot" | "dynamics";
type ConnStatus = "disconnected" | "connected" | "error";

interface ProviderConfig {
  id: Provider;
  name: string;
  brandColor: string;
  description: string;
}

const PROVIDERS: ProviderConfig[] = [
  {
    id: "salesforce",
    name: "Salesforce",
    brandColor: "#0B0B0F",
    description: "Sync accounts, opportunities, contacts and activities from Sales Cloud.",
  },
  {
    id: "hubspot",
    name: "HubSpot",
    brandColor: "#FF7A59",
    description: "Sync companies, deals, contacts and engagements from Sales Hub.",
  },
  {
    id: "dynamics",
    name: "MS Dynamics",
    brandColor: "#0B0B0F",
    description: "Sync accounts, opportunities, contacts and activities from Dynamics 365 Sales.",
  },
];

// ─── Status pill ──────────────────────────────────────────────────────────────

function StatusPill({ status }: { status: ConnStatus }) {
  if (status === "connected") {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full uppercase font-semibold"
        style={{
          background: "#DCFCE7",
          color: "#166534",
          fontSize: 11,
          padding: "4px 10px",
          letterSpacing: "0.06em",
        }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#16A34A" }} />
        Connected
      </span>
    );
  }
  if (status === "error") {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full uppercase font-semibold"
        style={{
          background: "#FEE2E2",
          color: "#991B1B",
          fontSize: 11,
          padding: "4px 10px",
          letterSpacing: "0.06em",
        }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#DC2626" }} />
        Sync error
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full uppercase font-semibold"
      style={{
        background: "#EFEFEE",
        color: "#525258",
        fontSize: 11,
        padding: "4px 10px",
        letterSpacing: "0.06em",
      }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#8E8E94" }} />
      Disconnected
    </span>
  );
}

// ─── Logo block ───────────────────────────────────────────────────────────────

function ProviderLogo({ p }: { p: ProviderConfig }) {
  // Text fallback in the brand color — a real SVG can drop in here later.
  return (
    <div
      className="flex items-center justify-center rounded-lg flex-shrink-0 font-black"
      style={{
        background: "#FFFFFF",
        border: `2px solid ${p.brandColor}`,
        color: p.brandColor,
        width: 48,
        height: 48,
        fontSize: 14,
        letterSpacing: "0.02em",
      }}
      aria-label={p.name}
    >
      {p.name === "Salesforce" ? "SF" : p.name === "HubSpot" ? "HS" : "D365"}
    </div>
  );
}

// ─── Provider card ────────────────────────────────────────────────────────────

interface CardProps {
  p: ProviderConfig;
  status: ConnStatus;
  onConnect: () => void;
}

function ProviderCard({ p, status, onConnect }: CardProps) {
  return (
    <div
      className="bg-white rounded-xl p-6 flex flex-col"
      style={{ border: "1px solid #E5E7EB", minHeight: 240 }}
    >
      <div className="flex items-start gap-4 mb-4">
        <ProviderLogo p={p} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold mb-0.5" style={{ color: "#0B0B0F", fontSize: 16 }}>
            {p.name}
          </div>
          <div style={{ fontSize: 12, color: "#6B6B70" }}>{p.description}</div>
        </div>
      </div>

      <div className="mb-5">
        <StatusPill status={status} />
      </div>

      <div className="mt-auto">
        <button
          onClick={onConnect}
          className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
          style={{ background: "#0B0B0F" }}
        >
          Connect {p.name}
        </button>
      </div>
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function ConnectModal({
  provider,
  onClose,
}: {
  provider: ProviderConfig;
  onClose: () => void;
}) {
  // Esc key dismissal
  useEffect(() => {
    function handleEsc(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4"
      style={{ background: "rgba(15,23,42,0.55)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full"
        style={{ border: "1px solid #E5E7EB" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-2">
          <div className="flex items-start gap-4 mb-4">
            <ProviderLogo p={provider} />
            <div className="flex-1">
              <div
                className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1"
                style={{ color: "#DC2626" }}
              >
                Pilot setup
              </div>
              <div className="font-bold text-lg" style={{ color: "#0B0B0F" }}>
                Connect {provider.name}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-full text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-colors flex-shrink-0"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
          <p style={{ color: "#525258", fontSize: 14, lineHeight: 1.5 }}>
            CRM integration is available in your pilot. Talk to us to enable read-and-write sync,
            owner mapping and field-level outbound rules.
          </p>
        </div>
        <div
          className="px-6 py-4 flex items-center gap-3 justify-end"
          style={{ borderTop: "1px solid #EFEFEE", background: "#FAFAF7" }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ border: "1px solid #E5E7EB", color: "#525258", background: "#FFFFFF" }}
          >
            Close
          </button>
          <a
            href="mailto:info@mi6group.com?subject=Pilot%20CRM%20integration"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: "#0B0B0F" }}
          >
            Schedule a call
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Integrations() {
  const [openProvider, setOpenProvider] = useState<Provider | null>(null);

  // For now every provider is disconnected. When wired, replace with live state
  // pulled from /api/integrations or the `integrations` table keyed by tenant.
  const [cardState] = useState<Record<Provider, ConnStatus>>({
    salesforce: "disconnected",
    hubspot: "disconnected",
    dynamics: "disconnected",
  });

  const handleConnect = useCallback((id: Provider) => {
    setOpenProvider(id);
  }, []);

  const activeProvider = openProvider ? PROVIDERS.find((p) => p.id === openProvider) : null;

  return (
    <OperatorShell breadcrumbs={[{ label: "Integrations" }]} pageTitle="Integrations" pageMeta="CRM connections">

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        {/* Header */}
        <div>
          <div
            className="text-[10px] uppercase tracking-[0.28em] font-bold mb-2"
            style={{ color: "#DC2626" }}
          >
            Settings — Integrations
          </div>
          <h1 className="text-2xl font-bold mb-1" style={{ color: "#0B0B0F" }}>
            Integrations
          </h1>
          <p style={{ color: "#525258", fontSize: 14, maxWidth: 720 }}>
            Your CRM is the ledger. Attilius is the playbook. Same accounts, two purposes,
            one source of truth per field.
          </p>
        </div>

        {/* Connection cards */}
        <div
          data-tour="crm-card"
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {PROVIDERS.map((p) => (
            <ProviderCard
              key={p.id}
              p={p}
              status={cardState[p.id]}
              onConnect={() => handleConnect(p.id)}
            />
          ))}
        </div>

        {/* Mailbox capture — Tier 1 Activity Capture (Outlook + Gmail) */}
        <IntegrationsPanel />

        {/* How sync works */}
        <div
          className="rounded-xl bg-white p-6 space-y-5"
          style={{ border: "1px solid #E5E7EB" }}
        >
          <div
            className="text-xs font-bold uppercase tracking-[0.2em]"
            style={{ color: "#0B0B0F" }}
          >
            How sync works
          </div>

          <div className="space-y-4" style={{ fontSize: 14, color: "#3F3F44", lineHeight: 1.6 }}>
            <div>
              <div className="font-semibold mb-1" style={{ color: "#0B0B0F" }}>
                Inbound — read-only by default
              </div>
              <p>
                Attilius pulls accounts, contacts, opportunities, activities and owner mappings
                from your CRM on a schedule. Nothing is written back unless you opt in.
                If a field exists in both systems, the CRM wins on inbound sync.
              </p>
            </div>

            <div>
              <div className="font-semibold mb-1" style={{ color: "#0B0B0F" }}>
                Outbound — opt-in per field
              </div>
              <p>
                Pick which Attilius signals you want pushed back to your CRM: CP score deltas,
                coaching outcomes, completed touches, decision-maker map updates and
                forecast-bucket recommendations. Each field is opt-in. No silent overwrites.
              </p>
            </div>

            <div>
              <div className="font-semibold mb-1" style={{ color: "#0B0B0F" }}>
                System of record
              </div>
              <p>
                The CRM owns opportunity name, value, stage and close date — those flow one
                way, CRM to Attilius. Attilius owns CP statuses, qualification answers, coaching
                notes, cadence outcomes and the decision-maker map — those stay in Attilius and
                only sync out on explicit opt-in. No field has two owners.
              </p>
            </div>
          </div>
        </div>

        {/* Footnote */}
        <div style={{ fontSize: 12, color: "#8E8E94", textAlign: "center" }}>
          Need a CRM that is not listed?{" "}
          <a
            href="mailto:info@mi6group.com?subject=Custom%20CRM%20integration"
            style={{ color: "#0B0B0F", textDecoration: "underline" }}
          >
            Tell us which one
          </a>
          .
        </div>
      </div>

      {activeProvider && (
        <ConnectModal provider={activeProvider} onClose={() => setOpenProvider(null)} />
      )}
    </OperatorShell>
  );
}
